import { GET_BELEGUNGEN } from '@/domain/dienstplan/features/VisualEditor/hooks/useBelegungen/graphql/queries';
import {
  Belegung,
  BelegungsIntervall,
} from '@/domain/dienstplan/shared/types/Belegung';
import { Weekday } from '@/domain/dienstplan/shared/types/Weekday';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import {
  Dienstplan_GetBelegungenQuery,
  Dienstplan_GetBelegungenQueryVariables,
  Dienstplan_Tag,
} from '@/gql/graphql';
import { useSuspenseQuery } from '@apollo/client';

export function useBelegungen(tag: Weekday): BelegungsIntervall[] {
  const standorte = useStandortContext();
  const result = useSuspenseQuery<
    Dienstplan_GetBelegungenQuery,
    Dienstplan_GetBelegungenQueryVariables
  >(GET_BELEGUNGEN, {
    variables: {
      tag: mapWeekdayToDienstplanTag(tag),
      standortId: standorte.currentStandort.id,
    },
  });

  const belegunsIntervalle = result.data.dienstplan_findAllBelegungen.map(
    (belegung) => {
      return new BelegungsIntervall(
        new Belegung({
          id: belegung.id,
          tag: mapDienstplanTagToWeekday(belegung.wochentag),
          start: timeStringToNumber(belegung.von),
          ende: timeStringToNumber(belegung.von) + belegung.dauer / 60,
          personenanzahlMin: 0,
          personenanzahlMax: belegung.anzahlMitarbeiter,
          arbeitsbereich: {
            id: belegung.arbeitsbereich.id,
            markierung: belegung.arbeitsbereich.markierung,
            name: belegung.arbeitsbereich.name,
          },
        }),
        'inactive',
      );
    },
  );

  return belegunsIntervalle;
}

// eslint-disable-next-line complexity
export function mapWeekdayToDienstplanTag(tag: Weekday): Dienstplan_Tag {
  if (tag === 'montag') {
    return Dienstplan_Tag.Montag;
  }
  if (tag === 'dienstag') {
    return Dienstplan_Tag.Dienstag;
  }
  if (tag === 'mittwoch') {
    return Dienstplan_Tag.Mittwoch;
  }
  if (tag === 'donnerstag') {
    return Dienstplan_Tag.Donnerstag;
  }
  if (tag === 'freitag') {
    return Dienstplan_Tag.Freitag;
  }
  if (tag === 'samstag') {
    return Dienstplan_Tag.Samstag;
  }
  if (tag === 'sonntag') {
    return Dienstplan_Tag.Sonntag;
  }
  if (tag === 'feiertag') {
    return Dienstplan_Tag.Feiertag;
  }
  throw new Error(`Unknown weekday: ${tag}`);
}

// eslint-disable-next-line complexity
export function mapDienstplanTagToWeekday(tag: Dienstplan_Tag): Weekday {
  if (tag === Dienstplan_Tag.Montag) {
    return 'montag';
  }
  if (tag === Dienstplan_Tag.Dienstag) {
    return 'dienstag';
  }
  if (tag === Dienstplan_Tag.Mittwoch) {
    return 'mittwoch';
  }
  if (tag === Dienstplan_Tag.Donnerstag) {
    return 'donnerstag';
  }
  if (tag === Dienstplan_Tag.Freitag) {
    return 'freitag';
  }
  if (tag === Dienstplan_Tag.Samstag) {
    return 'samstag';
  }
  if (tag === Dienstplan_Tag.Sonntag) {
    return 'sonntag';
  }
  if (tag === Dienstplan_Tag.Feiertag) {
    return 'feiertag';
  }
  throw new Error(`Unknown weekday: ${tag}`);
}

export function timeStringToNumber(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours + minutes / 60;
}
